import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import CCOContainer from "../../fundamentals/CCOContainer";
import {Button, Divider, Grid} from "@mui/material";
import CCOParagraph from "../../fundamentals/CCOParagraph";
import GiftCertificatePreview from "../../forms/F007ModularDonationForm/sections/F007GiftForm/GiftCertificatePreview";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import React, {useEffect} from "react";
import {useFormContext} from "react-hook-form";
import {DonationProductFormData} from "../../forms/F007ModularDonationForm/types";
import {useTranslation} from "react-i18next";
import {SosDonationProduct} from "../../../../packages/graphql-layer/src";
import {getCertificate, getQrCodeLink, ProductState} from "./P020DonationProduct";
import {redirectTo} from "../../../utility/navigationUtil";
import {inputProps, wrapperSx} from "./P020DonationProductStyles";
import {
    CM_P_ID,
    PRODUCT_FIRSTNAME,
    PRODUCT_GREETING,
    PRODUCT_QUANTITY,
    PRODUCT_SURNAME,
    useSetDefaultValue
} from "../../forms/F007ModularDonationForm/utils";
import {useSessionContext} from "../../../context/SessionContext";
import {usePageContext} from "../../../context/PageContext";
import {FormInputTextField} from "../../forms/F007ModularDonationForm/inputs/FormInputTextField";
import {useRouter} from "next/router";
import CCOHeadlineAsText from "../../fundamentals/CCOHeadlineAsText";

export interface Props {
    product: SosDonationProduct
}
export const SESSION_PARAM_PRODUCT_FORM_DATA = 'DonationProductFormData';

const getDefault = () => ({
    [PRODUCT_SURNAME]: '',
    [PRODUCT_FIRSTNAME]: '',
    [PRODUCT_GREETING]: '',
    [PRODUCT_QUANTITY]: 1,
});

export function getCertificateViewType(product: SosDonationProduct | undefined) {
    if (!product) {
        return '';
    }
    const donationCertificates = product?.donationCertificate;
    if (donationCertificates && donationCertificates.length > 0) {
        return donationCertificates[0]?.viewtype || '';
    }
    return '';
}

function P020DonationProductForm(props: Props) {
    const router = useRouter();
    // Default Werte für das Formular
    const {watch} = useFormContext<DonationProductFormData>();
    const defaultSessionValues = getDefault();
    useSetDefaultValue(PRODUCT_QUANTITY, defaultSessionValues[PRODUCT_QUANTITY]);
    useSetDefaultValue(CM_P_ID, props.product.id);

    // Aus dem Pageconext werden die Labels und Einstellungen für das Formular geholt
    const {t} = useTranslation();
    const pageContext = usePageContext();
    const donationProductShopSettings = pageContext?.page?.donationProductShopSettings;
    const submitLabel = donationProductShopSettings?.donationProductPageSubmitLabel || t('p020_product_label_Submit');
    const choseAmount = donationProductShopSettings?.donationProductPageChoseAmountLabel || t("p020_product_amount");
    const greetingsLabel = donationProductShopSettings?.donationProductPageGreetingsLabel || t("p020_product_greeting");
    const gifRecipientLabel = donationProductShopSettings?.donationProductPageGiftRecipientLabel || t("p020_product_gifted");

    // In der Session merken wir uns beim Submit die Formulardaten, damit sie beim Rücksprung wieder vorausgefüllt sind
    const {setValue, handleSubmit} = useFormContext<DonationProductFormData>();
    const session = useSessionContext();
    // QR Code und Zertifikat
    const qrCodeLink = getQrCodeLink(props.product);
    const certificate = getCertificate(props.product);
    const productQuantity = watch(PRODUCT_QUANTITY) as number;
    const productFirstname = watch(PRODUCT_FIRSTNAME);
    const productSurname = watch(PRODUCT_SURNAME);
    const productGreeting = watch(PRODUCT_GREETING);
    const isValid = productQuantity && (productQuantity >= 1) && productFirstname && productSurname;
    const naturalPositiveNumber = new RegExp('^[1-9][0-9]*$');

    // Zum prefill des Formulars, wenn man vom Spendenformular zurück kommt
    useEffect(() => {
        const storedData = session.getJsonFromSession(SESSION_PARAM_PRODUCT_FORM_DATA) as ProductState;
        if (storedData) {
            const greeting = storedData.productGreeting ? storedData.productGreeting : '';
            const firstName = storedData.productFirstname ? storedData.productFirstname : '';
            const surname = storedData.productSurname ? storedData.productSurname : '';
            const quantity = storedData.productSurname ? storedData.productQuantity : 1;
            setValue(PRODUCT_GREETING, greeting);
            setValue(PRODUCT_FIRSTNAME, firstName);
            setValue(PRODUCT_SURNAME, surname);
            setValue(PRODUCT_QUANTITY, quantity);
        }
        // eslint-disable-next-line
    }, []);

    // Falls der User die Seite verlässt, wird die eingegebene Menge auf 1 gesetzt
    useEffect(() => {
        const handleRouteChange = () => {
            const storedData = session.getJsonFromSession(SESSION_PARAM_PRODUCT_FORM_DATA) as ProductState;
            let state = storedData ? storedData : defaultSessionValues;
            state.productQuantity = 1;
            session.addJsonToSession(SESSION_PARAM_PRODUCT_FORM_DATA, state);
        };
        router.events.on("routeChangeStart", handleRouteChange);
        return () => {
            router.events.off("routeChangeStart", handleRouteChange);
        }
        // eslint-disable-next-line
    }, [router]);

    /**
     * Erstellt die Weiterleitungs-URL
     * @param forwardUrl
     */
    function getForwardUrl(forwardUrl: string) {
        const parameters = new URLSearchParams();
        parameters.append(CM_P_ID, props.product.id);
        return forwardUrl + '?' + parameters.toString();
    }

    function onQuantityChange(event: any) {
        let newQuantity = event.target.value;
        if (naturalPositiveNumber.test(newQuantity) || newQuantity === "") {
            setValue(PRODUCT_QUANTITY, newQuantity);
        } else {
            // Bei ungültiger Eingabe wird der Defaultwert gesetzt
            setValue(PRODUCT_QUANTITY, defaultSessionValues[PRODUCT_QUANTITY]);
        }
    }

    const onSubmit = async () => {
        const forwardUrl = getForwardUrl(donationProductShopSettings?.donationProductDonationFormLink || '');
        const state = {
            productQuantity: productQuantity,
            productGreeting: productGreeting,
            productFirstname: productFirstname,
            productSurname: productSurname
        } as ProductState;
        session.addJsonToSession(SESSION_PARAM_PRODUCT_FORM_DATA, state);
        redirectTo(forwardUrl);
    };

    return <form data-cypress='donationProductForm' method='GET'>
        <CCOComponentWrapper>
            <CCOContainer width='large'>
                <CCOContainer>
                    <Grid container spacing={4} columns={8} alignItems='center'>
                        <Grid item xs={8} md={6} lg={6}>
                            <CCOHeadlineAsText textAlign='left' title={choseAmount} variant='h4' />
                        </Grid>
                        <Grid item xs={8} md={2} lg={2}>
                            <FormInputTextField
                                name={PRODUCT_QUANTITY}
                                type='number'
                                InputProps={inputProps}
                                label=''
                                sx={wrapperSx}
                                pattern={naturalPositiveNumber}
                                onChange={onQuantityChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={8} md={8} lg={8} marginTop={2}>
                            <CCOHeadlineAsText title={t('p020_product_form_headline')} textAlign='left' variant='h4' />
                        </Grid>
                        <Grid item xs={8} md={8} lg={8}>
                            <CCOParagraph sx={{marginBottom: 0}}>{greetingsLabel!}</CCOParagraph>
                        </Grid>
                        <Grid item xs={8} md={8} lg={8}>
                            <FormInputTextField
                                name={PRODUCT_GREETING}
                                label=''
                                placeholder={t('p020_product_label_greeting')!}
                            />
                        </Grid>
                        <Grid item xs={8} md={8} lg={8}>
                            <CCOParagraph sx={{marginBottom: 0}}>{gifRecipientLabel!}</CCOParagraph>
                        </Grid>
                        <Grid item xs={8} md={8} lg={8}>
                            <FormInputTextField
                                name={PRODUCT_FIRSTNAME}
                                label=''
                                placeholder={t('p020_product_label_firstName')!}
                                required={true}
                            />
                        </Grid>
                        <Grid item xs={8} md={8} lg={8} marginBottom={3}>
                            <FormInputTextField
                                name={PRODUCT_SURNAME}
                                label=''
                                placeholder={t('p020_product_label_lastName')!}
                                required={true}
                            />
                        </Grid>
                        <Grid item xs={8} md={4} lg={4} marginTop={2} marginBottom={2}>
                            <GiftCertificatePreview pdfCertificate={certificate!}
                                                    qrCodeLink={qrCodeLink || undefined}
                                                    isValid={!!isValid}
                                                    isProduct
                            />
                        </Grid>
                        <Grid item xs={8} md={4} lg={4} marginTop={2} marginBottom={2}>
                            <Button type='button'
                                    size='large'
                                    variant='contained'
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={!isValid}
                                    data-cypress='product-donation-submit'
                                    fullWidth>{submitLabel}</Button>
                        </Grid>
                        <Grid item xs={8} md={8} lg={8}>
                            <Divider/>
                        </Grid>
                        <Grid item xs={8} md={8} lg={8}>
                            <CCORichText metadataId={props.product.id} textAsTree={props.product.details?.textAsTree} propertyName='properties.details'/>
                        </Grid>
                    </Grid>
                </CCOContainer>
            </CCOContainer>
        </CCOComponentWrapper>
    </form>
}

export default P020DonationProductForm;